import React, {useEffect, useState} from 'react';
import {
    DefaultButton,
    IContextualMenuProps,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    Stack,
    TooltipHost
} from "@fluentui/react";
import {INewProduct, IProductImage} from "../../models/Search";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import BasicDetails from "./BasicDetails";
import BasicDetailsTable from "./BasicDetailsTable";
import {AttributesTable, IAttributeKV} from "./AttributesTable";
import {ProductLinesTable} from "./ProductLinesTable";
import EditorVendorTable from "./EditableVendorsTable";
import {usePostProductMutation} from "../../store/Api";
import EditableImage from "./EditableImage";
import {IsMutationSuccess} from "../../logic/MutationTypeChecker";
import {IActionResponse} from "../../dal/BaseDAL";

interface INewProductModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (productId: string) => void;
}

const defaultProduct: INewProduct = {
    manufacturer: '',
    manufacturerPartNumber: '',
    name: '',
    description: '',
    vendors: [],
    tags: [],
    links: [],
    images: [],
    filters: {},
    facets: {},
    attributes: {},
    priceLevels: [],
    productLines: [],
    favorite: {company: false, user: false},
}

const NewProductModal: React.FC<INewProductModalProps> = (props) => {
    const [saving, setSaving] = React.useState(false);
    const catalog = useSelector((state: RootState) => state.search.selectedCatalog);
    const [product, setProduct] = React.useState<INewProduct>(defaultProduct);
    const [attrArray, setAttrArray] = useState<IAttributeKV[]>([]);
    const [facetArray, setFacetArray] = useState<IAttributeKV[]>([]);
    const [filterArray, setFilterArray] = useState<IAttributeKV[]>([]);
    const [productLines, setProductLines] = useState<string[]>([])
    const [updatedProductImageUrl, setProductImageUrl] = useState<string>();
    const [postProduct] = usePostProductMutation();
    const [saveable, setSaveable] = useState(false);

    useEffect(() => {
        setProduct(defaultProduct);
    }, [props.open])
    
    useEffect(() => {
        if( product.name.trim() !== '' && product.manufacturerPartNumber?.trim() !== '') {
            setSaveable(true);
        }
        else {
            setSaveable(false);
        }
    }, [product])
    
    const save = (close?: boolean) => {
        setSaving(true);
        postProduct({
            catalog: catalog?.name ?? 'technology',
            product: {
                ...product,
                image: updatedProductImageUrl,
                attributes: Object.assign({}, ...attrArray.map((x) => ({[x.key]: x.value}))),
                facets: Object.assign({}, ...facetArray.map((x) => ({[x.key]: x.value}))),
                filters: Object.assign({}, ...filterArray.map((x) => ({[x.key]: x.value}))),
                productLines,
            }
        }).then((response) => {
            setSaving(false);
            if(close) {
                reset();
                props.onClose();
            }
            else if (IsMutationSuccess<IActionResponse>(response)) {
                props.onSave(response.data.id);
            }
        });
    }

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save(true);
                }
            }
        ],
    };

    const productImage: IProductImage = {
        name: "Product Image",
        url: updatedProductImageUrl ?? '',
        type: "Product",
        id: 'NEW',
        isCustom: true
    };
    
    const reset = () => {
        setProduct(defaultProduct);
        setAttrArray([]);
        setFacetArray([])
        setFilterArray([])
        setProductLines([])
        setProductImageUrl(undefined)
    }

    return (
        <Panel
            className={"detail-panel"}
            headerText="New Product"
            isOpen={props.open}
            onDismiss={() => {props.onClose(); reset();}}
            type={PanelType.large}
            styles={{
                commands: {
                    background: "white"
                },
                footer: {
                    background: "white"
                },
                navigation: {
                    flex: '1 1 auto',
                },
            }}
            onRenderHeader={() => {
                return (
                    <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
                        <Stack horizontal disableShrink>
                            <Stack.Item align='auto'>
                                <EditableImage image={productImage} editMode={true}
                                               onChange={(a, b) => setProductImageUrl(b)}/>
                            </Stack.Item>
                        </Stack>
                    </div>
                )
            }}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <div style={{background: "white"}}>
                    { saveable ?
                        <DefaultButton
                            text={saving ? "Saving" : "Save"}
                            iconProps={{iconName: 'Save'}}
                            disabled={saving}
                            primary
                            onClick={()=> save(false)}
                            split
                            splitButtonAriaLabel="More save actions"
                            menuProps={menuProps}
                        />
                        : <TooltipHost
                            content="Name and Manufacturer Part Number are required to save the product."
                        >
                            <DefaultButton
                                text={saving ? "Saving" : "Save"}
                                iconProps={{iconName: 'Save'}}
                                disabled={!saveable || saving}
                                primary
                                onClick={()=> save(false)}
                                split
                                splitButtonAriaLabel="More save actions"
                                menuProps={menuProps}
                            />
                        </TooltipHost>
                    }
                    <DefaultButton
                        text={"Cancel"}
                        iconProps={{iconName: 'cancel'}}
                        disabled={saving}
                        onClick={() => {
                            props.onClose(); 
                            reset()}
                        }
                        style={{marginLeft: '1em'}}
                    />
                </div>
            )}
        >
            <Pivot
                overflowBehavior='menu'
                styles={{itemContainer: {marginTop: '1em'}}}
                defaultSelectedKey={'Attributes'}
            >
                <PivotItem headerText='Details' itemKey={'Details'}>
                    <h3>Description</h3>
                    <BasicDetails
                        id={'NEW'}
                        product={product}
                        onChange={setProduct}
                        editMode={true}
                    />
                </PivotItem>
                <PivotItem headerText='Attributes' itemKey={'Attributes'}>
                    <BasicDetailsTable
                        product={product}
                        editMode={true}
                        onChange={setProduct}
                        catalog={catalog?.name ?? ''}
                    />
                    {((product.name && product.name != '') && (product.manufacturerPartNumber && product.manufacturerPartNumber != '')) && 
                    <React.Fragment>
                        <AttributesTable
                            title='Attributes'
                            attributes={attrArray}
                            onUpdate={setAttrArray}
                            editMode={true}
                        />
                        <AttributesTable
                            title='Facets'
                            attributes={facetArray}
                            onUpdate={setFacetArray}
                            editMode={true}
                        />
                        <AttributesTable
                            title='Filters'
                            attributes={filterArray}
                            onUpdate={setFilterArray}
                            editMode={true}
                        />
                        <ProductLinesTable
                            title='Product Lines'
                            productLines={productLines}
                            editMode={true}
                            onUpdate={setProductLines}
                        />
                    </React.Fragment>}
                </PivotItem>
                <PivotItem headerText='Vendor' itemKey={'Vendor'} >
                    <EditorVendorTable product={product} onChange={setProduct}/>
                </PivotItem>
            </Pivot>
        </Panel>
    )
}

export default NewProductModal;
